import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';
import { Button, Form, Col, Row, Alert } from 'react-bootstrap';

const EditClosingRule = () => {
  const { id } = useParams();
  const [form, setForm] = useState({
    origin_port_list: '',
    destination_port_list: '',
    ports_of_loading: '',
    ports_of_discharge: '',
    doc_cutoff_days: '',
    cfs_cutoff_days: '',
    booking_cutoff_days: '',
    eta_days: '',
    for_carrier_scac_list: '', // Field for SCAC codes
    for_carrier_string_service_loop_list: ''
  });

  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState([]);
  const [dischargeSuggestions, setDischargeSuggestions] = useState([]);
  const [scacSuggestions, setScacSuggestions] = useState([]); // Autosuggestions for SCAC codes
  const [originList, setOriginList] = useState([]);
  const [destinationList, setDestinationList] = useState([]);
  const [loadingList, setLoadingList] = useState([]);
  const [dischargeList, setDischargeList] = useState([]);
  const [scacList, setScacList] = useState([]); // List of SCAC codes for form
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/api/closing-rule/${id}`);
        const data = response.data[0];

        setForm({
          origin_port_list: data.origin_port_list || '',
          destination_port_list: data.destination_port_list || '',
          ports_of_loading: data.ports_of_loading || '',
          ports_of_discharge: data.ports_of_discharge || '',
          doc_cutoff_days: data.doc_cutoff_days || '',
          cfs_cutoff_days: data.cfs_cutoff_days || '',
          booking_cutoff_days: data.booking_cutoff_days || '',
          eta_days: data.eta_days || '',
          for_carrier_scac_list: data.for_carrier_scac_list || '', // Populate saved SCAC values
          for_carrier_string_service_loop_list: data.for_carrier_string_service_loop_list || ''
        });

        setOriginList(data.origin_port_list ? data.origin_port_list.split(',') : []);
        setDestinationList(data.destination_port_list ? data.destination_port_list.split(',') : []);
        setLoadingList(data.ports_of_loading ? data.ports_of_loading.split(',') : []);
        setDischargeList(data.ports_of_discharge ? data.ports_of_discharge.split(',') : []);
        setScacList(data.for_carrier_scac_list ? data.for_carrier_scac_list.split(',') : []); // Populate SCAC list

      } catch (error) {
        console.error('Error fetching closing rule', error);
        setErrorMessage('Error fetching closing rule');
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value
    }));
  };

  const handleSuggestionFetch = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/port_code_extended_autosuggest', { params: { query: value } });
      setSuggestions(response.data.map((item) => item.port_code_suggestion));
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Fetch SCAC data for autosuggestions from the API
  const handleScacSuggestionFetch = async ({ value }) => {
    try {
      const response = await axios.get('/api/data');
      const suggestions = response.data
        .filter((item) => item.provider_display_name.toLowerCase().includes(value.toLowerCase()))
        .map((item) => ({
          scac: item.provider_scac,
          displayName: item.provider_display_name
        }));
      setScacSuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching SCAC data', error);
    }
  };

  const handleAddToList = (value, list, setList) => {
    if (value && !list.includes(value)) {
      setList([...list, value]);
    }
    setForm((prevForm) => ({
      ...prevForm,
      origin_port_list: '',
      destination_port_list: '',
      ports_of_loading: '',
      ports_of_discharge: '',
      for_carrier_scac_list: '',
      for_carrier_string_service_loop_list: ''
    }));
  };

  const handleRemoveFromList = (value, list, setList) => {
    setList(list.filter((item) => item !== value));
  };

  const inputProps = (name, value, onChange) => ({
    placeholder: 'Enter a Location Code',
    value,
    name,
    onChange
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (originList.length === 0 || destinationList.length === 0) {
      setErrorMessage('Origin(s) and Destination(s) are required.');
      return;
    }

    const formData = {
      ...form,
      origin_port_list: originList.join(','),
      destination_port_list: destinationList.join(','),
      ports_of_loading: loadingList.join(','),
      ports_of_discharge: dischargeList.join(','),
      for_carrier_scac_list: scacList.join(','), // Save SCAC list as a comma-delimited string
      for_carrier_string_service_loop_list: form.for_carrier_string_service_loop_list,
      doc_cutoff_days: form.doc_cutoff_days ? parseInt(form.doc_cutoff_days, 10) : null,
      cfs_cutoff_days: form.cfs_cutoff_days ? parseInt(form.cfs_cutoff_days, 10) : null,
      booking_cutoff_days: form.booking_cutoff_days ? parseInt(form.booking_cutoff_days, 10) : null,
      eta_days: form.eta_days ? parseInt(form.eta_days, 10) : null
    };

    try {
      await axios.put(`/api/closing-rule/${id}`, formData);
      navigate('/rules/closing', { state: { successMessage: 'Closing rule changes successfully saved' } });
    } catch (error) {
      console.error('Error saving closing rule', error);
      setErrorMessage('Error: Closing rule changes could not be saved');
    }
  };

  return (
    <div>
      <h2>Edit Closing Rule</h2>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
          {errorMessage}
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        {/* SCAC List Autosuggest */}
        <Form.Group as={Row}>
          <Form.Label column sm={2}>Carrier SCAC List</Form.Label>
          <Col sm={10}>
            <Autosuggest
              suggestions={scacSuggestions}
              onSuggestionsFetchRequested={handleScacSuggestionFetch}
              onSuggestionsClearRequested={() => setScacSuggestions([])}
              getSuggestionValue={(suggestion) => suggestion.scac}
              renderSuggestion={(suggestion) => <div>{suggestion.displayName}</div>}
              inputProps={inputProps('for_carrier_scac_list', form.for_carrier_scac_list, handleChange)}
              onSuggestionSelected={(e, { suggestion }) => handleAddToList(suggestion.scac, scacList, setScacList)}
            />
            <div className="suggestion-list">
              {scacList.map((item, index) => (
                <div key={index} className="suggestion-pill">
                  {item} <Button variant="danger" size="sm" onClick={() => handleRemoveFromList(item, scacList, setScacList)}>x</Button>
                </div>
              ))}
            </div>
          </Col>
        </Form.Group>

        {/* Origin Port List */}
        <Form.Group as={Row}>
          <Form.Label column sm={2}>Applies to Origin(s)</Form.Label>
          <Col sm={10}>
            <Autosuggest
              suggestions={originSuggestions}
              onSuggestionsFetchRequested={({ value }) => handleSuggestionFetch(value, setOriginSuggestions)}
              onSuggestionsClearRequested={() => setOriginSuggestions([])}
              getSuggestionValue={(suggestion) => suggestion}
              renderSuggestion={(suggestion) => <div>{suggestion}</div>}
              inputProps={inputProps('origin_port_list', form.origin_port_list, handleChange)}
              onSuggestionSelected={(e, { suggestionValue }) => handleAddToList(suggestionValue, originList, setOriginList)}
            />
            <div className="suggestion-list">
              {originList.map((item, index) => (
                <div key={index} className="suggestion-pill">
                  {item} <Button variant="danger" size="sm" onClick={() => handleRemoveFromList(item, originList, setOriginList)}>x</Button>
                  </div>
                ))}
              </div>
            </Col>
          </Form.Group>

          {/* Destination Port List */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>Applies to Destination(s)</Form.Label>
            <Col sm={10}>
              <Autosuggest
                suggestions={destinationSuggestions}
                onSuggestionsFetchRequested={({ value }) => handleSuggestionFetch(value, setDestinationSuggestions)}
                onSuggestionsClearRequested={() => setDestinationSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion}
                renderSuggestion={(suggestion) => <div>{suggestion}</div>}
                inputProps={inputProps('destination_port_list', form.destination_port_list, handleChange)}
                onSuggestionSelected={(e, { suggestionValue }) => handleAddToList(suggestionValue, destinationList, setDestinationList)}
              />
              <div className="suggestion-list">
                {destinationList.map((item, index) => (
                  <div key={index} className="suggestion-pill">
                    {item} <Button variant="danger" size="sm" onClick={() => handleRemoveFromList(item, destinationList, setDestinationList)}>x</Button>
                  </div>
                ))}
              </div>
            </Col>
          </Form.Group>

          {/* Ports of Loading */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>Ports of Loading</Form.Label>
            <Col sm={10}>
              <Autosuggest
                suggestions={loadingSuggestions}
                onSuggestionsFetchRequested={({ value }) => handleSuggestionFetch(value, setLoadingSuggestions)}
                onSuggestionsClearRequested={() => setLoadingSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion}
                renderSuggestion={(suggestion) => <div>{suggestion}</div>}
                inputProps={inputProps('ports_of_loading', form.ports_of_loading, handleChange)}
                onSuggestionSelected={(e, { suggestionValue }) => handleAddToList(suggestionValue, loadingList, setLoadingList)}
              />
              <div className="suggestion-list">
                {loadingList.map((item, index) => (
                  <div key={index} className="suggestion-pill">
                    {item} <Button variant="danger" size="sm" onClick={() => handleRemoveFromList(item, loadingList, setLoadingList)}>x</Button>
                  </div>
                ))}
              </div>
            </Col>
          </Form.Group>

          {/* Ports of Discharge */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>Ports of Discharge</Form.Label>
            <Col sm={10}>
              <Autosuggest
                suggestions={dischargeSuggestions}
                onSuggestionsFetchRequested={({ value }) => handleSuggestionFetch(value, setDischargeSuggestions)}
                onSuggestionsClearRequested={() => setDischargeSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion}
                renderSuggestion={(suggestion) => <div>{suggestion}</div>}
                inputProps={inputProps('ports_of_discharge', form.ports_of_discharge, handleChange)}
                onSuggestionSelected={(e, { suggestionValue }) => handleAddToList(suggestionValue, dischargeList, setDischargeList)}
              />
              <div className="suggestion-list">
                {dischargeList.map((item, index) => (
                  <div key={index} className="suggestion-pill">
                    {item} <Button variant="danger" size="sm" onClick={() => handleRemoveFromList(item, dischargeList, setDischargeList)}>x</Button>
                  </div>
                ))}
              </div>
            </Col>
          </Form.Group>

          {/* Document Cutoff Days */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>Document Cutoff Days</Form.Label>
            <Col sm={10}>
              <Form.Control type="number" name="doc_cutoff_days" value={form.doc_cutoff_days} onChange={handleChange} />
            </Col>
          </Form.Group>

          {/* CFS Cutoff Days */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>CFS Cutoff Days</Form.Label>
            <Col sm={10}>
              <Form.Control type="number" name="cfs_cutoff_days" value={form.cfs_cutoff_days} onChange={handleChange} />
            </Col>
          </Form.Group>

          {/* Booking Cutoff Days */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>Booking Cutoff Days</Form.Label>
            <Col sm={10}>
              <Form.Control type="number" name="booking_cutoff_days" value={form.booking_cutoff_days} onChange={handleChange} />
            </Col>
          </Form.Group>

          {/* ETA Days */}
          <Form.Group as={Row}>
            <Form.Label column sm={2}>ETA Days</Form.Label>
            <Col sm={10}>
              <Form.Control type="number" name="eta_days" value={form.eta_days} onChange={handleChange} />
            </Col>
          </Form.Group>

          <Button variant="primary" type="submit">Save</Button>
          <Button variant="secondary" onClick={() => navigate('/rules/closing')}>Cancel</Button>
        </Form>
      </div>
    );
  };

  export default EditClosingRule;
